import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from 'aws-amplify/api';
import { formatTimeAgo, formatDateTime } from '../utils/dateUtils';
import { setDocumentTitle } from '../utils/titleManager';
import LoadingSpinner from '../components/LoadingSpinner';
import InlineSpinner from '../components/InlineSpinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import '../styles/management.css';


const DEBUG = true;

function EventListScreen() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingMock, setCreatingMock] = useState(false);

  useEffect(() => {
    setDocumentTitle('Events');
  }, []);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  // Remove hasLoaded state as we don't need it
  const navigate = useNavigate();
  const shouldFetch = useRef(true);

  useEffect(() => {
    if (shouldFetch.current) {
      shouldFetch.current = false;
      fetchEvents();
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      DEBUG && console.log('Fetching events...');
      const restOperation = get({
        apiName: 'events',
        path: '/events',
        withCredentials: true
      });
      const { body } = await restOperation.response;
      const bodyJSON = await body.json();

      DEBUG && console.log("API response:", bodyJSON);
      // Parse the JSON string in data.body
      // not necessary now the whole string is json
      //const bodyData = JSON.parse(bodyJSON);
      // Now we can safely access the events
      const eventsList = Array.isArray(bodyJSON?.events) ? bodyJSON.events : [];
      DEBUG && console.log("Setting events array:", eventsList);

      setEvents(eventsList);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  const createMockEvent = async () => {
    try {
      setCreatingMock(true);
      DEBUG && console.log('Creating mock event...');
      
      const restOperation = post({
        apiName: 'events',
        path: '/events/mock',
        withCredentials: true,
        options: {
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        }
      });
      
      const { body } = await restOperation.response;
      const bodyJSON = await body.json();
      
      DEBUG && console.log("Mock event created:", bodyJSON);
      
      // Refresh the events list
      fetchEvents();
      setCreatingMock(false);
    } catch (err) {
      console.error("Error creating mock event:", err);
      setError(`Error creating mock event: ${err.message}`);
      setCreatingMock(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading events..." />;
  if (error) return <div className="message message--error">Error: {error}</div>;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedEvents = () => {
    return [...events].sort((a, b) => {
      let aValue = a[sortField] || a.createdAt;
      let bValue = b[sortField] || b.createdAt;
      
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  return (
    <div className="events">
      <h2>Your Events</h2>
      <div className="button-group" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button 
          className="button button--primary"
          onClick={() => navigate('/events/create')}
        >
          Create New Event
        </button>
        <button 
          className="button button--secondary"
          onClick={createMockEvent}
          disabled={creatingMock}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '5px',
            backgroundColor: creatingMock ? '#cccccc' : '#4a90e2',
            cursor: creatingMock ? 'not-allowed' : 'pointer'
          }}
        >
          <AutoFixHighIcon style={{ fontSize: '20px' }} />
          {creatingMock ? 'Creating...' : 'Create Mock Event'}
        </button>
      </div>
      <div className="events__list">
        {loading && (
          <div style={{textAlign: 'center', padding: '20px'}}><InlineSpinner message="Loading events..." /></div>
        )}
        {error && (
          <p>Error: {error}</p>
        )}
        {!loading && !error && (!events?.length) && (
          <p>No events found. Create an event to get started!</p>
        )}
        {!loading && !error && events?.length > 0 && (
          <div className="table-container">
            <table className="events-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')}>
                    Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('date')}>
                    Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('location')}>
                    Location {sortField === 'location' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('updatedAt')}>
                    Last Updated {sortField === 'updatedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('createdAt')}>
                    Created {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Attendance</th>
                </tr>
              </thead>
              <tbody>
                {getSortedEvents().map(event => (
                  <tr
                    key={event.id}
                    className="event-row"
                    onClick={() => navigate(`/events/${event.id}`)}
                  >
                    <td>{event.name}</td>
                    <td>{event.date}</td>
                    <td>{event.location}</td>
                    <td>{formatTimeAgo(event.updatedAt)}</td>
                    <td>{formatDateTime(event.createdAt)}</td>
                    <td className="attendance-stats">
                      <div className="attendance-icons">
                        <div className="attendance-stat">
                          <CheckCircleIcon style={{ color: '#4CAF50' }} />
                          <span>{event.attendance_stats?.attending || 0}</span>
                        </div>
                        <div className="attendance-stat">
                          <CancelIcon style={{ color: '#F44336' }} />
                          <span>{event.attendance_stats?.not_attending || 0}</span>
                        </div>
                        <div className="attendance-stat">
                          <VisibilityIcon style={{ color: '#2196F3' }} />
                          <span>{event.attendance_stats?.viewed || 0}</span>
                        </div>
                        <div className="attendance-stat">
                          <VisibilityOffIcon style={{ color: '#FFC107' }} />
                          <span>{event.attendance_stats?.not_viewed || 0}</span>
                        </div>
                      </div>
                      <div className="attendance-total">
                        Total Invited: {event.attendance_stats?.total_invited || 0}
                        {event.attendance_stats?.not_invited > 0 && (
                          <span> | Not Invited: {event.attendance_stats?.not_invited}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventListScreen;












